import React from "react";
import {useSortable} from "@dnd-kit/react/sortable";
import {FaClock} from "react-icons/fa6";
import CardIndicators from "./card-indicators";
import {getLabelColorStyle, getLabelSolidColorClass} from "./card-details-modal/label-utils";
import InputRadio from "../../../components/input-radio";

const DemandCard = ({card, index, listId, users = [], onToggleCompleted, onOpen, isDragDisabled}) => {
    const {ref, isDragging} = useSortable({
        id: card.id,
        index,
        group: listId,
        type: "demand-card",
        accept: "demand-card",
        data: {card, listId},
        disabled: isDragDisabled,
    });
    const labels = card.labels ?? [];
    const assignees = card.assignees ?? [];
    const hasDueDate = Boolean(card.dueDate && card.dueDate !== "Sem prazo");
    const getAssignee = assignee => users.find(user => String(user.value) === String(assignee));
    const getInitials = assignee => {
        const name = getAssignee(assignee)?.label ?? String(assignee);
        return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase();
    };

    const handleKeyDown = event => {
        if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) return;
        event.preventDefault();
        onOpen();
    };

    return (
        <article ref={ref} role="button" tabIndex={0} onClick={onOpen} onKeyDown={handleKeyDown} className={`group relative rounded-lg border bg-white p-3 shadow-sm transition-shadow hover:shadow-md ${isDragDisabled ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"} ${isDragging ? "z-50 border-blue-400 opacity-45 shadow-lg" : "border-slate-200"}`}>
            {labels.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                    {labels.map((label, labelIndex) => <span key={label.id ?? `${label.text}-${label.color}-${labelIndex}`} title={label.text || "Etiqueta sem nome"} style={getLabelColorStyle(label.color)} className={`inline-flex h-6 min-w-8 items-center justify-center rounded-full px-2 text-center text-[11px] font-semibold ${getLabelSolidColorClass(label.color)}`}>{label.text || <span className="sr-only">Etiqueta sem nome</span>}</span>)}
                </div>
            )}

            <div className="flex items-start">
                {!isDragDisabled && <div onClick={event => event.stopPropagation()}
                    onPointerDown={event => event.stopPropagation()}
                    onKeyDown={event => event.stopPropagation()}
                    className="max-w-0 shrink-0 overflow-hidden opacity-0 transition-[max-width,margin,opacity] duration-200 ease-out group-hover:mr-2 group-hover:max-w-4 group-hover:opacity-100 group-focus-within:mr-2 group-focus-within:max-w-4 group-focus-within:opacity-100">
                    <InputRadio
                        id={`demand-card-completed-${card.id}`}
                        name={`demand-card-completed-${card.id}`}
                        checked={Boolean(card.completed)}
                        onChange={() => {}}
                        onClick={onToggleCompleted}
                        label={card.completed ? "Reabrir cartão" : "Marcar como concluído"}
                        className="mt-0.5 w-4 shrink-0 [&_input]:cursor-pointer [&_input]:accent-emerald-600 [&_label]:sr-only"
                        title={"Marcar como concluído"}
                    />
                </div>}
                <h3 className={`min-w-0 flex-1 text-sm font-semibold leading-5 ${card.completed ? "text-slate-500 line-through" : "text-slate-800"}`}>{card.title}</h3>
            </div>
            {card.description && <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-slate-500">{card.description.replace(/<[^>]*>/g, " ")}</p>}
            <CardIndicators card={card} className="mt-2" />

            {(hasDueDate || assignees.length > 0) && (
                <footer className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5 text-xs text-slate-500">
                    {hasDueDate && (
                        <span className={`flex items-center gap-1 ${card.dueDate === "Hoje" ? "font-semibold text-amber-600" : ""}`}>
                            <FaClock />{card.dueDate}
                        </span>
                    )}
                    <div className="ml-auto flex -space-x-1.5">
                        {assignees.map((assignee, avatarIndex) => <span key={`${assignee}-${avatarIndex}`} title={getAssignee(assignee)?.label ?? String(assignee)} className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-slate-700 text-[9px] font-bold text-white">{getInitials(assignee)}</span>)}
                    </div>
                </footer>
            )}
        </article>
    );
};

export default DemandCard;
